import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useConnectionStore } from '@/stores/connectionStore';
import { useBillingStore } from '@/stores/billingStore';
import { useAuthStore } from '@/stores/authStore';
import { CheckoutSlideOver } from '@/components/billing/CheckoutSlideOver';
import { ManageSlideOver } from '@/components/billing/ManageSlideOver';
import { CheckCircle, AlertCircle, X, CreditCard, Sparkles, Crown, Loader2, User, Mail, Check } from 'lucide-react';

type OAuthStatus = { type: 'success'; account: string; purpose?: string } | { type: 'error'; message: string } | null;

// Steps shown in the Zoom Connection setup timeline (full-account-setup roadmap).
const ZOOM_SETUP_STEPS: { title: string; description: string }[] = [
  { title: 'Sign in to Zoom', description: 'Log in to your Zoom admin account in the guided browser.' },
  { title: 'Choose access level', description: 'Read-only, or read + write access.' },
  { title: 'Create the connection app', description: 'ZAC provisions a Server-to-Server app on your account.' },
  { title: 'Account connected', description: 'ZAC can read your Zoom configuration and assist.' },
];

type SetupStepStatus = 'done' | 'active' | 'todo';

// Map AUTHORITATIVE backend state onto the 4-step roadmap. Driven by the
// connection store (real credentials), NOT the ephemeral client setup store —
// so a fresh/wiped account shows zero progress (step 1 active), never stale
// green ticks. `connected` = credentials exist (readable) → the whole roadmap
// is complete; until then only the first step is "up next".
function zoomStepStatus(index: number, connected: boolean): SetupStepStatus {
  if (connected) return 'done';
  return index === 0 ? 'active' : 'todo';
}

export function SettingsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [oauthStatus, setOauthStatus] = useState<OAuthStatus>(null);
  const {
    checkConnection,
    fetchCapabilities,
  } = useConnectionStore();
  const isConnected = useConnectionStore((s) => s.isConnected);
  const capabilities = useConnectionStore((s) => s.capabilities);
  // "Connected" for the roadmap = ZAC can actually read the account (real
  // credentials), regardless of stale client-side setup flags.
  const zoomConnected = isConnected || !!capabilities?.canRead;
  const navigate = useNavigate();
  const {
    billingEnabled, plan, subscriptionStatus, seats, currentPeriodEnd, trialEnd, cancelAtPeriodEnd,
    fetchStatus, verifySession, createCheckout, isTrialing, daysLeftInTrial,
  } = useBillingStore();
  const user = useAuthStore((s) => s.user);
  const billingRef = useRef<HTMLDivElement>(null);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [checkoutSecret, setCheckoutSecret] = useState<string | null>(null);
  const [manageOpen, setManageOpen] = useState(false);

  // Fetch connection state on mount
  useEffect(() => {
    checkConnection();
    fetchCapabilities();
    fetchStatus();
  }, [checkConnection, fetchCapabilities, fetchStatus]);

  // Handle OAuth callback query params
  useEffect(() => {
    const oauth = searchParams.get('oauth');
    const account = searchParams.get('account');
    const message = searchParams.get('message');
    const purpose = searchParams.get('purpose');

    if (oauth === 'success' && account) {
      setOauthStatus({ type: 'success', account, purpose: purpose ?? undefined });
      setSearchParams({}, { replace: true });
      checkConnection();
      fetchCapabilities();
      const timer = setTimeout(() => setOauthStatus(null), 5000);
      return () => clearTimeout(timer);
    } else if (oauth === 'error') {
      setOauthStatus({ type: 'error', message: message ?? 'OAuth connection failed' });
      setSearchParams({}, { replace: true });
      const timer = setTimeout(() => setOauthStatus(null), 10000);
      return () => clearTimeout(timer);
    }
  }, [searchParams, setSearchParams, checkConnection, fetchCapabilities]);

  // Handle billing query params
  const [billingMessage, setBillingMessage] = useState<{ type: 'success' | 'cancel'; text: string } | null>(null);

  useEffect(() => {
    const billing = searchParams.get('billing');
    const sessionId = searchParams.get('session_id');
    const tab = searchParams.get('tab');

    if (billing === 'success' && sessionId) {
      setSearchParams({}, { replace: true });
      setBillingMessage({ type: 'success', text: 'Activating your subscription...' });

      verifySession(sessionId).then((activated) => {
        setBillingMessage({
          type: 'success',
          text: activated
            ? 'Your subscription is now active!'
            : 'Subscription is being processed. It may take a moment to activate.',
        });
        setTimeout(() => setBillingMessage(null), 5000);
      });
    } else if (billing === 'success') {
      // No session_id — fall back to a status refresh
      setSearchParams({}, { replace: true });
      fetchStatus().then(() => {
        if (useBillingStore.getState().plan === 'pro') {
          setBillingMessage({ type: 'success', text: 'Your subscription is now active!' });
        }
        setTimeout(() => setBillingMessage(null), 5000);
      });
    } else if (billing === 'cancel') {
      setBillingMessage({ type: 'cancel', text: 'Checkout was cancelled.' });
      setSearchParams({}, { replace: true });
      const timer = setTimeout(() => setBillingMessage(null), 5000);
      return () => clearTimeout(timer);
    }

    if (tab === 'billing') {
      billingRef.current?.scrollIntoView({ behavior: 'smooth' });
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams, fetchStatus, verifySession]);

  return (
    <div className="flex-1 overflow-y-auto">
      {/* OAuth status banner */}
      {oauthStatus?.type === 'success' && (
        <div className="bg-green-900/30 border-b border-green-700/50 px-4 py-2">
          <div className="mx-auto max-w-3xl flex items-center justify-between">
            <div className="flex items-center gap-2 text-green-400">
              <CheckCircle className="h-4 w-4" />
              <span className="text-sm">
                Successfully connected {oauthStatus.purpose === 'writer' ? 'write access' : 'read access'} for {oauthStatus.account}
              </span>
            </div>
            <button onClick={() => setOauthStatus(null)} className="text-green-400/70 hover:text-green-400">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {oauthStatus?.type === 'error' && (
        <div className="bg-red-900/30 border-b border-red-700/50 px-4 py-2">
          <div className="mx-auto max-w-3xl flex items-center justify-between">
            <div className="flex items-center gap-2 text-red-400">
              <AlertCircle className="h-4 w-4" />
              <span className="text-sm">OAuth error: {oauthStatus.message}</span>
            </div>
            <button onClick={() => setOauthStatus(null)} className="text-red-400/70 hover:text-red-400">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      <div className="mx-auto max-w-3xl px-4 py-8">
        <h1 className="text-2xl font-semibold text-foreground mb-2">Settings</h1>
        <p className="text-muted-foreground mb-8">Manage your account, connections, and billing.</p>

        <div className="space-y-10">
          {/* Account Information Section */}
          <section>
            <h2 className="text-lg font-semibold text-foreground mb-1">Account</h2>
            <p className="text-sm text-muted-foreground mb-4">Your profile and account details.</p>
            <AccountInfoCard user={user} />
          </section>

          {/* Zoom Connection Section */}
          <section>
            <h2 className="text-lg font-semibold text-foreground mb-1">Zoom Connection</h2>
            <p className="text-sm text-muted-foreground mb-4">Connect your Zoom account to enable account management features.</p>
            <button
              onClick={() => navigate('/chat?setup=zoom')}
              className="w-full bg-[#2D8CFF] hover:bg-[#2681F0] text-white px-6 py-3 rounded-lg font-medium transition-colors"
            >
              Click here to setup zoom connection
            </button>

            {/* Setup timeline — reflects real connection state (not stale client flags) */}
            <ol className="mt-6 space-y-0">
              {ZOOM_SETUP_STEPS.map((step, i) => {
                const status = zoomStepStatus(i, zoomConnected);
                const isDone = status === 'done';
                const isActive = status === 'active';
                const isLast = i === ZOOM_SETUP_STEPS.length - 1;
                return (
                  <li key={step.title} className="relative flex gap-4 pb-6 last:pb-0">
                    {!isLast && (
                      <span
                        className={`absolute left-[15px] top-8 h-[calc(100%-1.5rem)] w-px ${isDone ? 'bg-green-500/50' : 'bg-border'}`}
                        aria-hidden="true"
                      />
                    )}
                    <span
                      className={`relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border text-sm font-medium ${
                        isDone
                          ? 'border-green-500 bg-green-500/15 text-green-400'
                          : isActive
                            ? 'border-[#2D8CFF] bg-[#2D8CFF]/15 text-[#2D8CFF]'
                            : 'border-border bg-card text-muted-foreground'
                      }`}
                    >
                      {isDone ? <Check className="h-4 w-4" /> : i + 1}
                    </span>
                    <div className="pt-1">
                      <p className={`text-sm font-medium ${isDone || isActive ? 'text-foreground' : 'text-muted-foreground'}`}>
                        {step.title}
                      </p>
                      <p className="text-xs text-muted-foreground">{step.description}</p>
                    </div>
                  </li>
                );
              })}
            </ol>
          </section>

          {/* Billing Section — hidden while billing is switched off */}
          {billingEnabled && (
          <section ref={billingRef}>
            <h2 className="text-lg font-semibold text-foreground mb-1">Billing</h2>
            <p className="text-sm text-muted-foreground mb-4">Manage your subscription and plan.</p>

            {subscriptionStatus === 'past_due' && (
              <div className="mb-4 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 flex items-center gap-3">
                <AlertCircle className="h-4 w-4 shrink-0 text-red-400" />
                <p className="flex-1 text-sm text-red-300">
                  Your last payment failed. Update your payment method to keep Pro access.
                </p>
                <button
                  onClick={() => { setManageOpen(true); }}
                  className="shrink-0 rounded-md bg-red-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-600 transition-colors"
                >
                  Update Payment
                </button>
              </div>
            )}

            {billingMessage && (
              <div className={`mb-4 rounded-lg border px-4 py-2.5 flex items-center gap-2 ${
                billingMessage.type === 'success'
                  ? 'border-green-700/50 bg-green-900/30 text-green-400'
                  : 'border-border bg-card text-muted-foreground'
              }`}>
                {billingMessage.type === 'success' ? <CheckCircle className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
                <span className="text-sm">{billingMessage.text}</span>
              </div>
            )}

            <BillingCard
              plan={plan}
              subscriptionStatus={subscriptionStatus}
              seats={seats}
              currentPeriodEnd={currentPeriodEnd}
              trialEnd={trialEnd}
              cancelAtPeriodEnd={cancelAtPeriodEnd}
              isTrialing={isTrialing()}
              daysLeft={daysLeftInTrial()}
              onUpgrade={async () => {
                const secret = await createCheckout(0, false);
                if (!secret) throw new Error('checkout');
                setCheckoutSecret(secret);
                setCheckoutOpen(true);
              }}
              onManage={async () => {
                setManageOpen(true);
              }}
            />
          </section>
          )}

        </div>
      </div>

      <CheckoutSlideOver
        open={checkoutOpen}
        clientSecret={checkoutSecret}
        onClose={() => { setCheckoutOpen(false); setCheckoutSecret(null); }}
      />

      <ManageSlideOver
        open={manageOpen}
        onClose={() => setManageOpen(false)}
      />
    </div>
  );
}

// =============================================================================
// Account Info Card Component
// =============================================================================

function AccountInfoCard({ user }: { user: { id: string; email: string; name: string | null; avatarUrl: string | null } | null }) {
  if (!user) return null;

  return (
    <div className="rounded-lg border border-border bg-card p-5">
      <div className="flex items-start gap-4">
        {user.avatarUrl ? (
          <img
            src={user.avatarUrl}
            alt={user.name ?? 'Avatar'}
            className="h-12 w-12 rounded-full object-cover"
          />
        ) : (
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-secondary text-muted-foreground">
            <User className="h-5 w-5" />
          </div>
        )}
        <div className="min-w-0 flex-1 space-y-2">
          {user.name && (
            <div className="flex items-center gap-2 text-sm">
              <User className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              <span className="text-foreground font-medium">{user.name}</span>
            </div>
          )}
          <div className="flex items-center gap-2 text-sm">
            <Mail className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <span className="text-muted-foreground">{user.email}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Billing Card Component
// =============================================================================

interface BillingCardProps {
  plan: string;
  subscriptionStatus: string | null;
  seats: number;
  currentPeriodEnd: string | null;
  trialEnd: string | null;
  cancelAtPeriodEnd: boolean;
  isTrialing: boolean;
  daysLeft: number | null;
  onUpgrade: () => Promise<void>;
  onManage: () => Promise<void>;
}

function BillingCard({
  plan, subscriptionStatus, seats, currentPeriodEnd, cancelAtPeriodEnd,
  isTrialing, daysLeft, onUpgrade, onManage,
}: BillingCardProps) {
  const isPro = plan === 'pro';
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleUpgrade = async () => {
    setLoading(true);
    setError(null);
    try {
      await onUpgrade();
    } catch {
      setError('Failed to start checkout. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleManage = async () => {
    setLoading(true);
    setError(null);
    try {
      await onManage();
    } catch {
      setError('Failed to open billing portal. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`rounded-lg border p-5 ${isPro ? 'border-primary/30 bg-primary/5' : 'border-border bg-card'}`}>
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3">
          <div className={`mt-0.5 rounded-full p-1.5 ${isPro ? 'bg-primary/20 text-primary' : 'bg-secondary text-muted-foreground'}`}>
            {isPro ? <Crown className="h-4 w-4" /> : <CreditCard className="h-4 w-4" />}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-medium text-foreground">
                {isPro ? 'Pro Plan' : 'Free Plan'}
              </h3>
              {isTrialing && daysLeft !== null && (
                <span className="rounded-full bg-primary/10 border border-primary/20 px-2 py-0.5 text-xs font-medium text-primary">
                  Trial — {daysLeft} days left
                </span>
              )}
              {cancelAtPeriodEnd && (
                <span className="rounded-full bg-yellow-500/10 border border-yellow-500/20 px-2 py-0.5 text-xs font-medium text-yellow-400">
                  Cancels {currentPeriodEnd ? new Date(currentPeriodEnd).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'at period end'}
                </span>
              )}
              {subscriptionStatus === 'past_due' && (
                <span className="rounded-full bg-red-500/10 border border-red-500/20 px-2 py-0.5 text-xs font-medium text-red-400">
                  Payment issue
                </span>
              )}
            </div>
            {isPro ? (
              <div className="mt-1.5 space-y-1 text-sm text-muted-foreground">
                <p>$50/mo base{seats > 0 ? ` + ${seats} additional seat${seats === 1 ? '' : 's'} ($${seats}/mo)` : ''}</p>
                {currentPeriodEnd && (
                  <p>
                    {cancelAtPeriodEnd ? 'Subscription ends' : 'Next billing'}:{' '}
                    {new Date(currentPeriodEnd).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                  </p>
                )}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground mt-1">
                Ask anything about Zoom. Upgrade to Pro for account management, reports, and more.
              </p>
            )}
            {error && (
              <p className="text-sm text-red-400 mt-2 flex items-center gap-1.5">
                <AlertCircle className="h-3.5 w-3.5" />
                {error}
              </p>
            )}
          </div>
        </div>
        <div className="shrink-0">
          {isPro ? (
            <button
              onClick={handleManage}
              disabled={loading}
              className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-sm font-medium text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Manage
            </button>
          ) : (
            <button
              onClick={handleUpgrade}
              disabled={loading}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
              Upgrade to Pro
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

